import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { useGameStore } from "../store/useGameStore";
import { useMountsStore } from "../store/useMountsStore";
import {
  getMountIconPath,
  MOUNT_RARITY_COLOR,
} from "../utils/assets";
import type { MountCatalogEntry, PlayerMountView } from "../api/mounts";
import { ModalBackdrop } from "../components/ModalBackdrop";

export function MountsScreen() {
  const { hero, setScreen } = useGameStore();
  const { state, loading, actionLoading, error, message, refresh, train, clearMessage } =
    useMountsStore();
  const [selected, setSelected] = useState<{ entry: MountCatalogEntry; mount: PlayerMountView } | null>(
    null,
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const unlockLevel = state?.unlock_level ?? 16;
  const unlocked = state?.unlocked ?? (hero?.level ?? 0) >= unlockLevel;

  const openMount = useCallback(
    (entry: MountCatalogEntry, mount: PlayerMountView) => {
      setSelected({ entry, mount });
      clearMessage();
    },
    [clearMessage],
  );

  if (loading && !state) {
    return <div className="p-4 text-center text-gray-400">Loading mounts…</div>;
  }

  if (!unlocked) {
    return (
      <div className="p-4 space-y-4">
        <BackButton onBack={() => setScreen("main")} />
        <p className="text-gray-400 text-sm text-center">Mounts unlock at level {unlockLevel}.</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 pb-8">
      <BackButton onBack={() => setScreen("main")} />

      <div className="text-center">
        <h1 className="text-xl font-bold font-game text-gold-400">Mounts</h1>
        <p className="text-xs text-gray-400 mt-1">Collect and train mounts — stats stack on your hero.</p>
      </div>

      {state && (
        <div className="grid grid-cols-3 gap-2 text-center text-xs bg-kingdom-purple border border-kingdom-mid rounded-xl p-3">
          <StatChip label="Power" value={state.totals.power} />
          <StatChip label="Armor" value={state.totals.armor} />
          <StatChip label="Endurance" value={state.totals.endurance} />
        </div>
      )}

      <p className="text-xs text-gray-400 text-center">
        Training cap: {state?.totals.global_training_cap ?? 0} · Stones: {state?.training_stones ?? 0}
      </p>

      {error && (
        <div className="rounded-xl border border-rose-500/40 bg-rose-950/40 px-3 py-2 text-sm text-rose-300">
          {error}
        </div>
      )}
      {message && (
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-950/30 px-3 py-2 text-sm text-emerald-200">
          {message}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {state?.catalog.map((entry) => {
          const mount = state.mounts.find((m) => m.mount_code === entry.code)!;
          return (
            <MountCard
              key={entry.code}
              entry={entry}
              mount={mount}
              onClick={() => openMount(entry, mount)}
            />
          );
        })}
      </div>

      {selected && state && (
        <MountDetailModal
          entry={selected.entry}
          mount={selected.mount}
          stones={state.training_stones}
          globalCap={state.totals.global_training_cap}
          loading={actionLoading}
          onClose={() => setSelected(null)}
          onTrain={(stones) => void train(selected.entry.code, stones).then(() => {
            const updated = useMountsStore.getState().state?.mounts.find(
              (m) => m.mount_code === selected.entry.code,
            );
            if (updated) setSelected({ entry: selected.entry, mount: updated });
          })}
        />
      )}
    </div>
  );
}

function StatChip({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-gray-500">{label}</div>
      <div className="font-bold text-white">+{value}</div>
    </div>
  );
}

function MountCard({
  entry,
  mount,
  onClick,
}: {
  entry: MountCatalogEntry;
  mount: PlayerMountView;
  onClick: () => void;
}) {
  const rarityClass = MOUNT_RARITY_COLOR[entry.rarity] ?? MOUNT_RARITY_COLOR.common;
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "rounded-xl border-2 overflow-hidden text-left bg-kingdom-dark/80 active:scale-[0.98] transition-transform",
        mount.owned ? rarityClass : "border-kingdom-mid opacity-60",
      )}
    >
      <div className="aspect-[3/2] bg-black/40 relative">
        <img
          src={getMountIconPath(entry.code)}
          alt={entry.name}
          className={clsx("w-full h-full object-cover", !mount.owned && "grayscale brightness-50")}
        />
        {mount.owned && (
          <span className="absolute top-1 right-1 text-[10px] bg-black/60 px-1.5 py-0.5 rounded text-gold-400 font-bold">
            Lv.{mount.training_level}
          </span>
        )}
      </div>
      <div className="p-2">
        <div className="text-xs font-bold text-white truncate">{entry.name}</div>
        <div className="text-[10px] text-gray-500 capitalize">{entry.rarity}</div>
      </div>
    </button>
  );
}

function MountDetailModal({
  entry,
  mount,
  stones,
  globalCap,
  loading,
  onClose,
  onTrain,
}: {
  entry: MountCatalogEntry;
  mount: PlayerMountView;
  stones: number;
  globalCap: number;
  loading: boolean;
  onClose: () => void;
  onTrain: (stones: number) => void;
}) {
  const atCap = mount.training_level >= mount.effective_max_level;
  const pct =
    mount.xp_to_next > 0 ? Math.min(100, (mount.training_xp / mount.xp_to_next) * 100) : 100;

  return (
    <ModalBackdrop onClose={onClose}>
      <div className="w-full max-w-sm bg-kingdom-purple border border-kingdom-light rounded-2xl p-4 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="font-bold text-white">{entry.name}</h2>
            <p className="text-xs text-gray-400 capitalize">{entry.rarity}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 text-xl">
            ✕
          </button>
        </div>

        <img
          src={getMountIconPath(entry.code)}
          alt={entry.name}
          className="w-full aspect-[3/2] object-cover rounded-xl border border-kingdom-mid"
        />

        {!mount.owned ? (
          <p className="text-sm text-gray-400">Find a mount card to unlock this mount.</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <StatChip label="Power" value={mount.power} />
              <StatChip label="Armor" value={mount.armor} />
              <StatChip label="Endurance" value={mount.endurance} />
            </div>

            <div>
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>Training Lv.{mount.training_level}</span>
                <span>
                  {atCap
                    ? "Max for current cap"
                    : `${mount.training_xp} / ${mount.xp_to_next} XP`}
                </span>
              </div>
              <div className="h-2 rounded-full bg-kingdom-dark overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-gold-500 to-amber-400 transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <p className="text-[10px] text-gray-500 mt-1">
                Global training cap: {globalCap} (collect more mounts to raise)
              </p>
            </div>

            <div className="flex gap-2">
              {[1, 5, 10].map((n) => (
                <button
                  key={n}
                  type="button"
                  disabled={loading || atCap || stones < n}
                  onClick={() => onTrain(n)}
                  className="flex-1 py-2 rounded-lg bg-kingdom-mid text-white text-sm font-semibold disabled:opacity-40"
                >
                  Train ×{n}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-gray-500 text-center">Stones available: {stones}</p>
          </>
        )}
      </div>
    </ModalBackdrop>
  );
}

function BackButton({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="text-gray-400 hover:text-white border border-kingdom-mid px-3 py-1.5 rounded-lg text-sm"
    >
      ← Back
    </button>
  );
}
